/**
 * CalendarScopeDropdown — the Practitioner calendar's scope as Antonin's export draws it in the toolbar centre (22 Oct,
 * `CalendarToolbar.tsx` tone 'practitioner', DECISIONS "the scope is a dropdown ("All ▾"), as PM's Practitioners
 * control"): one brand-tinted button naming the scope, a chevron after it, the list opening on click. On Day and Week
 * it replaces the specialty chip row (`calendar-specialty-row.tsx`) with the same options and the same handlers: "All"
 * clears the store's specialty selection, any other option toggles its specialty (several may be on; each shows its
 * tick). The row's ≡ Provider bulk-select (Select all / Unselect all for the practitioners, each disabled when it would
 * change nothing) moves under the list in the same popover, so the button stands alone in the centre as in the export.
 *
 * The label names the scope: "All" with nothing chosen, the one specialty, or the first and "+N" for more.
 * Look: the bridge's "calendar design: scope dropdown" rules (`.cal-scope-*`) on the Day menu's item look.
 */
import { useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { AnchoredPopover, AnchoredPopoverContent, AnchoredPopoverTrigger } from '@scripsteam/scrips-signal-ds';
import { scopeLabel } from '@/scheduling/lib/calendar-scope-label';

const ALL = 'All';

export interface CalendarScopeDropdownProps {
  /** Option labels in display order, "All" first. */
  specialties: string[];
  selectedSpecialties: string[];
  onSpecialtyToggle: (specialty: string) => void;
  onSpecialtyClear: () => void;
  onSelectAll: () => void;
  onUnselectAll: () => void;
  allSelected?: boolean;
  noneSelected?: boolean;
}

export function CalendarScopeDropdown({
  specialties,
  selectedSpecialties,
  onSpecialtyToggle,
  onSpecialtyClear,
  onSelectAll,
  onUnselectAll,
  allSelected = false,
  noneSelected = false,
}: CalendarScopeDropdownProps) {
  const [open, setOpen] = useState(false);
  const value = scopeLabel(selectedSpecialties);
  const options = specialties.includes(ALL) ? specialties : [ALL, ...specialties];
  return (
    <AnchoredPopover open={open} onOpenChange={setOpen}>
      <AnchoredPopoverTrigger
        render={
          <button
            type="button"
            className="cal-scope-trigger"
            data-slot="pa-calendar-scope"
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-label={`Calendar scope: ${value}`}
          />
        }
      >
        <span className="cal-scope-value">{value}</span>
        <ChevronDown aria-hidden className="cal-scope-chevron" />
      </AnchoredPopoverTrigger>
      <AnchoredPopoverContent align="center" side="bottom" sideOffset={4} className="cal-day-menu cal-scope-menu">
        <div role="listbox" aria-label="Calendar scope" aria-multiselectable="true">
          {options.map((label) => {
            const isAll = label === ALL;
            const selected = isAll ? selectedSpecialties.length === 0 : selectedSpecialties.includes(label);
            return (
              <button
                key={label}
                type="button"
                role="option"
                aria-selected={selected}
                className="cal-day-menu-item cal-scope-option"
                onClick={() => {
                  if (isAll) {
                    onSpecialtyClear();
                    setOpen(false);
                  } else {
                    onSpecialtyToggle(label);
                  }
                }}
              >
                <span>{label}</span>
                {selected ? <Check aria-hidden className="cal-scope-tick" /> : null}
              </button>
            );
          })}
        </div>
        {/* Provider bulk-select (Flutter parity: selects or clears the practitioner checkboxes, not the specialties). */}
        <div role="menu" aria-label="Provider bulk-select" className="cal-scope-bulk">
          <button
            type="button"
            role="menuitem"
            disabled={allSelected}
            className="cal-day-menu-item"
            onClick={() => {
              onSelectAll();
              setOpen(false);
            }}
          >
            <span>Select all</span>
          </button>
          <button
            type="button"
            role="menuitem"
            disabled={noneSelected}
            className="cal-day-menu-item"
            onClick={() => {
              onUnselectAll();
              setOpen(false);
            }}
          >
            <span>Unselect all</span>
          </button>
        </div>
      </AnchoredPopoverContent>
    </AnchoredPopover>
  );
}
